import { onBeforeUnmount, ref, watch, type Ref } from "vue";

// ENG-20855: shared popover positioning for the date-picker components (MDate, MDateTime,
// MDatePicker). Extracted from the duplicated inline logic added in ENG-21034 — behaviour is
// identical (viewport-flip + click-outside + reposition-on-scroll), just shared and tested.

export interface PopupGeometry {
  rect: { top: number; bottom: number; left: number };
  popupHeight: number;
  popupWidth: number;
  viewportHeight: number;
  viewportWidth: number;
  /** gap between trigger and popup, px (default 2) */
  gap?: number;
  /** minimum distance from a viewport edge, px (default 8) */
  edgeMargin?: number;
}

const Z_INDEX = "9002";

// Pure: decide whether the popup opens below or flips above, and clamp horizontally.
export function computePopupStyle(geometry: PopupGeometry): Record<string, string> {
  const { rect, popupHeight, popupWidth, viewportHeight, viewportWidth } = geometry;
  const gap = geometry.gap ?? 2;
  const edge = geometry.edgeMargin ?? 8;

  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  const left = Math.max(edge, Math.min(rect.left, viewportWidth - popupWidth - edge));

  if (spaceBelow < popupHeight + gap && spaceAbove > spaceBelow) {
    return {
      position: "fixed",
      bottom: `${viewportHeight - rect.top + gap}px`,
      left: `${left}px`,
      zIndex: Z_INDEX,
    };
  }
  return {
    position: "fixed",
    top: `${rect.bottom + gap}px`,
    left: `${left}px`,
    zIndex: Z_INDEX,
  };
}

export interface UsePopupPositionOptions {
  /** open/visibility state of the popup (the component's `showForm`) */
  isOpen: Ref<boolean>;
  /** resolves the trigger element each reposition (component refs expose `$el`) */
  getTrigger: () => HTMLElement | null | undefined;
  gap?: number;
  edgeMargin?: number;
  repositionIntervalMs?: number;
}

export function usePopupPosition(options: UsePopupPositionOptions) {
  const { isOpen, getTrigger, gap, edgeMargin, repositionIntervalMs = 100 } = options;

  const popupEl = ref<HTMLElement | null>(null);
  const popupStyle = ref<Record<string, string>>({});

  function updatePosition() {
    const triggerEl = getTrigger();
    if (!triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();
    popupStyle.value = computePopupStyle({
      rect: { top: rect.top, bottom: rect.bottom, left: rect.left },
      popupHeight: popupEl.value?.offsetHeight ?? 350,
      popupWidth: popupEl.value?.offsetWidth ?? 290,
      viewportHeight: window.innerHeight,
      viewportWidth: window.innerWidth,
      gap,
      edgeMargin,
    });
  }

  function handleClickOutside(e: MouseEvent) {
    const triggerEl = getTrigger();
    if (popupEl.value?.contains(e.target as Node) || triggerEl?.contains(e.target as Node)) return;
    isOpen.value = false;
  }

  let intervalId: number | undefined;
  function teardown() {
    if (intervalId !== undefined) {
      window.clearInterval(intervalId);
      intervalId = undefined;
    }
    document.removeEventListener("mousedown", handleClickOutside);
  }

  watch(isOpen, (open, _old, onCleanup) => {
    if (!open) return;
    updatePosition();
    document.addEventListener("mousedown", handleClickOutside);
    intervalId = window.setInterval(updatePosition, repositionIntervalMs);
    onCleanup(teardown);
  });

  onBeforeUnmount(teardown);

  return { popupEl, popupStyle, updatePosition };
}
